import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteNotification, type Notification } from '@enterprise/api';
import { ensureError, findIndex, isDefined, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { notificationsQueryKeys } from '@feat/notifications/common/notifications-query-keys';
import { useApplicationStore } from '@hooks/application/application-store.hook';
import { useNotifier } from '@hooks/notifier/notifier.hook';

import type { DeleteNotificationArguments } from './types/delete-notification-arguments.type';
import type { NotificationsCacheContext } from './types/notifications-cache-context.type';

export const useDeleteNotification = () => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });

  const queryClient = useQueryClient();
  const notifier = useNotifier();
  const listKey = notificationsQueryKeys.list();

  return useMutation<unknown, Error, DeleteNotificationArguments, NotificationsCacheContext>({
    mutationFn: ({ id }) => {
      return deleteNotification(enterpriseApi, id);
    },
    onError: (e, _vars, context) => {
      if (isDefined(context)) {
        queryClient.setQueryData(listKey, context.snapshot);
      }

      notifier.error({ description: ensureError(e).message, title: 'Failed to delete notification' });
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const snapshot = queryClient.getQueryData<OperationResultWithData<Notification[]>>(listKey);

      queryClient.setQueryData<OperationResultWithData<Notification[]>>(listKey, (cached) => {
        if (isNotDefined(cached)) {
          return cached;
        }

        if (cached.failure) {
          return cached;
        }

        const { data } = cached;

        if (isNotDefined(data)) {
          return cached;
        }

        const index = findIndex(data, 'id', id);

        if (index < 0) {
          return cached;
        }

        return {
          ...cached,
          data: [...data.slice(0, index), ...data.slice(index + 1)],
        };
      });

      return { snapshot };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
};
